import { useEffect, useState } from 'react';
import { ADMIN_CONTENT_PADDING, ADMIN_CONTENT_PADDING_NARROW } from './panelLayout';

/**
 * The one media query behind the narrow (phone) layout: anything below antd's
 * `md` breakpoint (768px). A portrait tablet keeps the desktop layout; only
 * phones get the narrow one. The `.98` closes the gap a fractional viewport
 * width (browser zoom) would otherwise fall into between this and `md`.
 *
 * One explicit query rather than `Grid.useBreakpoint`: a partial antd
 * responsive map lets antd's own defaults win above its largest declared
 * breakpoint, and the jsdom `matchMedia` stub answers every query `false` —
 * which here means every existing test renders the desktop layout, unchanged.
 */
export const NARROW_VIEWPORT_QUERY = '(max-width: 767.98px)';

/**
 * Below antd's `lg` breakpoint (992px): a portrait tablet as well as a phone.
 * Only the header uses it, to drop the signed-in name, which together with the
 * console's tenant picker doesn't fit a tablet's width.
 */
export const COMPACT_HEADER_QUERY = '(max-width: 991.98px)';

function queryMatches(query: string): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(query).matches;
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => queryMatches(query));
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(query);
    setMatches(mq.matches);
    const onChange = () => setMatches(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [query]);
  return matches;
}

/**
 * Whether the viewport is phone-width, tracking rotation and resizes.
 *
 * Read synchronously on first render, unlike `usePrefersReducedMotion`: the
 * SPAs are client-only, and starting `false` would paint the desktop layout on
 * a phone for a frame before swapping it out.
 */
export function useNarrowViewport(): boolean {
  return useMediaQuery(NARROW_VIEWPORT_QUERY);
}

/** Whether the header should show the account as an icon only (see above). */
export function useCompactHeader(): boolean {
  return useMediaQuery(COMPACT_HEADER_QUERY);
}

/**
 * The padding AdminLayout puts around the routed page at the current width.
 * A page that cancels it to run edge to edge (the Batch screen) must cancel
 * this value, not the desktop constant, or it drifts on a phone.
 */
export function useAdminContentPadding(): number {
  return useNarrowViewport() ? ADMIN_CONTENT_PADDING_NARROW : ADMIN_CONTENT_PADDING;
}
